import React, { useEffect } from "react";
import { useUser } from "../contexts/useUser";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMCallsRecursive } from "../dataaccess/fetchLLMCalls";
import EvaluateResponseQuality from "./EvaluateResponseQuality";
import {
  Box,
  Button,
  Paper,
  Tabs,
  Tab,
  Toolbar,
  Typography,
} from "@mui/material";
import Calls from "./Calls";
import EvaluateBlockedPrompt from "./EvaluateBlockedPrompt";

interface ResponseQualityProps {}

const ResponseQuality: React.FC<ResponseQualityProps> = () => {
  const { project } = useUser();
  const [call, setCall] = React.useState(null);
  const [blockedCall, setBlockedCall] = React.useState(null);
  const [value, setValue] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const pageSize = 5;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [limit, setLimit] = React.useState("100");
  const [status, setStatus] = React.useState("200");

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "calls",
      { projectId: project?.id, status: status, limit: limit },
    ],
    queryFn: () =>
      fetchLLMCallsRecursive(project?.id ?? "", parseInt(limit), true, status),

    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  useEffect(() => {
    setPage(0);
    if (value === 1) {
      setStatus("403");
    } else {
      setStatus("200");
    }
  }, [value]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error...</div>;
  }

  const callsData = data?.data?.callsByProjectIdAndCreatedAt?.items ?? [];
  const paginatedData = callsData.slice(page * pageSize, (page + 1) * pageSize);

  const handleClick = (call: any) => {
    setCall(call);
  };

  const handleBlockedClick = (call: any) => {
    setBlockedCall(call);
  };

  return (
    <Box p={2}>
      <Tabs value={value} onChange={handleChange}>
        <Tab label="Evaluate Recent Responses" />
        <Tab label="Evaluate Blocked Prompts" />
      </Tabs>

      {value === 0 && (
        <Paper>
          <Box p={2}>
            <Typography variant="caption">
              Click on a recent response to evaluate and compare model quality.
            </Typography>
            <Calls
              calls={paginatedData}
              showFilter={false}
              title="Calls"
              onClick={handleClick}
              selectedCall={call}
            />

            <Box mt={2}>
              <Button
                onClick={() => setPage((prevPage) => Math.max(prevPage - 1, 0))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                onClick={() =>
                  setPage((prevPage) =>
                    Math.min(
                      prevPage + 1,
                      Math.ceil(callsData.length / pageSize) - 1
                    )
                  )
                }
                disabled={page === Math.ceil(callsData.length / pageSize) - 1}
              >
                Next
              </Button>
            </Box>
          </Box>
          <hr />

          {call && <EvaluateResponseQuality call={call} />}
        </Paper>
      )}

      {value === 1 && (
        <Paper>
          <Box p={2}>
            <Typography variant="caption">
              Click on a recent response to take action on blocked prompts
            </Typography>
            <Calls
              calls={paginatedData}
              showFilter={false}
              title="Blocked Calls"
              onClick={handleBlockedClick}
              selectedCall={blockedCall}
            />

            <Box mt={2}>
              <Button
                onClick={() => setPage((prevPage) => Math.max(prevPage - 1, 0))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                onClick={() =>
                  setPage((prevPage) =>
                    Math.min(
                      prevPage + 1,
                      Math.ceil(callsData.length / pageSize) - 1
                    )
                  )
                }
                disabled={page === Math.ceil(callsData.length / pageSize) - 1}
              >
                Next
              </Button>
            </Box>
          </Box>
          <hr />

          {blockedCall && <EvaluateBlockedPrompt call={blockedCall} />}
        </Paper>
      )}
    </Box>
  );
};

export default ResponseQuality;
